/**
 * AGT Predictive Search
 *
 * Unterschiede zur Theme-Version:
 * - Eigenes Debounce mit 150 ms statt globaler 300 ms.
 * - AbortController: laufende Requests werden verworfen, wenn weitergetippt wird.
 *   Verhindert, dass ein langsamer alter Request ein neueres Ergebnis ueberschreibt.
 * - Skeleton waehrend des Ladens, damit die Liste nicht springt.
 * - Leerer Zustand (Top-Produkte) beim Fokus ohne Eingabe.
 * - Mindestlaenge, damit nicht ab dem ersten Buchstaben gefeuert wird.
 * - resources[type] wird gesetzt. Ohne diesen Parameter liefert Shopify
 *   ausschliesslich Produkte, niemals Kategorien oder Seiten.
 * - keyup-Listener wird beim Schliessen wieder entfernt (war ein Leck).
 * - Tastaturnavigation ueber einen flachen Index statt Geschwister-Elementen,
 *   damit sie ueber Gruppengrenzen hinweg funktioniert.
 */

if (!customElements.get('predictive-search')) {
  const AGT_SEARCH = {
    /* Wartezeit nach dem letzten Tastendruck. 120-180 ms fuehlt sich direkt an,
       ohne bei jedem Buchstaben einen Request zu erzeugen. */
    debounceMs: 150,

    /* Ab wie vielen Zeichen gesucht wird.
       1 = Ergebnisse ab dem ersten Buchstaben. Kostet einen Request pro
       Tastendruck und liefert bei einem Zeichen sehr breite Treffer.
       2 = ruhiger und praeziser, aber "w" zeigt dann nichts. */
    minChars: 1,

    /* Welche Ergebnistypen Shopify liefern soll.
       Moeglich: product, collection, page, article, query
       WICHTIG: Ohne diesen Parameter liefert Shopify nur product. */
    resourceTypes: 'product,collection,page,query',

    /* Anzahl der Platzhalterzeilen im Skeleton. */
    skeletonRows: 4,
  };

  class PredictiveSearch extends HTMLElement {
    constructor() {
      super();
      this.cachedResults = {};
      this.input = this.querySelector('.js-search-input');
      this.productTypeSelect = this.querySelector('.js-search-product-types');
      this.productTypeInput = document.getElementById('product_type_input');
      this.resetBtn = this.querySelector('.js-search-reset');
      this.results = this.querySelector('.js-search-results');
      this.emptyState = this.querySelector('.js-search-empty');
      this.overlay = this.querySelector('.js-search-overlay');
      this.statusEl = this.querySelector('.js-search-status');
      this.loadingText = this.getAttribute('data-loading-text');

      this.abortController = null;
      this.debounceTimer = null;
      this.activeIndex = -1;

      this.addListeners();
    }

    disconnectedCallback() {
      this.abortRequest();
      clearTimeout(this.debounceTimer);
      this.close();
    }

    addListeners() {
      this.input.addEventListener('focus', this.handleFocus.bind(this));

      // Ohne Debounce: steuert nur die Sichtbarkeit von X und Lupe.
      // search-form.js macht das ebenfalls, aber mit 300 ms Verzoegerung -
      // dann blitzt die Lupe unter dem X durch.
      this.input.addEventListener('input', this.handleDirtyState.bind(this));

      this.input.addEventListener('input', this.handleInputDebounced.bind(this));

      if (this.productTypeSelect) {
        this.productTypeSelect.addEventListener('change', this.handleProductTypeChange.bind(this));
      }

      this.querySelector('.search').addEventListener('submit', this.handleSubmit.bind(this));
    }

    /**
     * Markiert das Feld als befuellt. Daran haengt in agt-search.css, ob
     * die Lupe ausgeblendet und das X eingeblendet wird.
     */
    handleDirtyState() {
      const filled = this.input.value.length > 0;
      this.input.classList.toggle('search__input--dirty', filled);
      this.resetBtn.hidden = !filled;
    }

    /**
     * Bricht einen laufenden Request ab.
     */
    abortRequest() {
      if (this.abortController) {
        this.abortController.abort();
        this.abortController = null;
      }
    }

    getQuery() {
      return this.input.value.trim();
    }

    handleSubmit(evt) {
      const selected = this.querySelector(
        '.predictive-search__item[aria-selected="true"]:not(.js-submit)'
      );
      if (!this.getQuery().length || selected) evt.preventDefault();
    }

    /**
     * Eigenes Debounce. Das globale debounce() liegt bei 300 ms und wird von
     * anderen Komponenten genutzt, deshalb wird es hier nicht angefasst.
     */
    handleInputDebounced() {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = setTimeout(this.handleInput.bind(this), AGT_SEARCH.debounceMs);
    }

    handleInput() {
      // autocomplete=off verhindert sonst, dass Safari den Wert nach dem
      // Zurueck-Button wiederherstellt.
      this.input.setAttribute('value', this.input.value);

      const searchTerm = this.getQuery();

      if (searchTerm.length < AGT_SEARCH.minChars) {
        this.abortRequest();
        this.showEmptyState();
        return;
      }

      this.getResults(searchTerm);
    }

    handleProductTypeChange(evt) {
      this.productTypeInput.value = evt.detail.selectedValue;
      const query = this.getQuery();
      if (query.length >= AGT_SEARCH.minChars) this.getResults(query);
    }

    handleFocus() {
      const searchTerm = this.getQuery();

      if (searchTerm.length < AGT_SEARCH.minChars) {
        this.showEmptyState();
        return;
      }

      if (this.getAttribute('results') === 'true') {
        this.showResults();
        this.open();
      } else {
        this.getResults(searchTerm);
      }
    }

    handleKeydown(evt) {
      switch (evt.key) {
        case 'ArrowUp':
        case 'ArrowDown':
          evt.preventDefault();
          if (this.hasAttribute('open')) this.handleResultsNav(evt.key);
          break;

        case 'Enter':
          this.selectOption();
          break;

        case 'Escape':
          this.close();
          this.input.blur();
          break;

        // no default
      }
    }

    handleKeyup() {
      if (!this.getQuery().length && this.dataset.state === 'results') {
        this.showEmptyState();
      }
    }

    /**
     * Liefert alle aktuell sichtbaren, navigierbaren Eintraege als flache Liste.
     * Geschwister-Navigation funktioniert nicht mehr, seit die Ergebnisse in
     * mehreren Gruppen liegen.
     */
    getNavigableItems() {
      const panel = this.dataset.state === 'empty' ? this.emptyState : this.results;
      if (!panel) return [];
      return Array.from(panel.querySelectorAll('.predictive-search__item'));
    }

    handleResultsNav(key) {
      const items = this.getNavigableItems();
      if (!items.length) return;

      if (key === 'ArrowDown') {
        this.activeIndex = this.activeIndex >= items.length - 1 ? 0 : this.activeIndex + 1;
      } else {
        this.activeIndex = this.activeIndex <= 0 ? items.length - 1 : this.activeIndex - 1;
      }

      items.forEach((item, i) => {
        item.setAttribute('aria-selected', i === this.activeIndex ? 'true' : 'false');
      });

      const active = items[this.activeIndex];
      active.scrollIntoView({ block: 'nearest' });

      this.statusEl.textContent = '';
      this.setLiveRegionText(active.textContent.trim());
      if (active.id) this.input.setAttribute('aria-activedescendant', active.id);
    }

    selectOption() {
      const selected = this.querySelector('[aria-selected="true"] .js-search-link');
      if (selected) selected.click();
    }

    /**
     * Baut die Query-Parameter fuer die Predictive-Search-API.
     */
    buildSearchParams(searchTerm) {
      let searchFields = 'title,product_type,variants.title,vendor';
      if (theme.settings.pSearchIncludeSkus) searchFields += ',variants.sku';
      if (theme.settings.pSearchIncludeTags) searchFields += ',tag';

      let params = '';
      if (this.productTypeInput && this.productTypeInput.value !== '') {
        params = `q=product_type:${encodeURIComponent(this.productTypeInput.value)} AND ${encodeURIComponent(searchTerm)}`;
      } else {
        params = `q=${encodeURIComponent(searchTerm)}`;
      }

      params += `&${encodeURIComponent('resources[type]')}=${AGT_SEARCH.resourceTypes}`;
      params += `&${encodeURIComponent('resources[limit]')}=${theme.settings.pSearchLimit}`;
      params += `&${encodeURIComponent('resources[limit_scope]')}=${theme.settings.pSearchLimitScope}`;
      params += `&${encodeURIComponent('resources[options][fields]')}=${searchFields}`;
      params += '&section_id=predictive-search';

      return params;
    }

    async getResults(searchTerm) {
      const searchParams = this.buildSearchParams(searchTerm);
      const queryKey = searchParams.replace(' ', '-').toLowerCase();

      // Treffer aus dem Cache kommen ohne Skeleton, sonst blitzt es unnoetig auf.
      if (this.cachedResults[queryKey]) {
        this.renderResults(this.cachedResults[queryKey]);
        return;
      }

      this.abortRequest();
      this.abortController = new AbortController();
      const { signal } = this.abortController;

      this.setLiveRegionLoadingState();
      this.showSkeleton();

      try {
        const response = await fetch(`${theme.routes.predictiveSearch}?${searchParams}`, { signal });
        if (!response.ok) throw new Error(response.status);

        const tmpl = document.createElement('template');
        tmpl.innerHTML = await response.text();

        const resultsEl = tmpl.content.querySelector('#shopify-section-predictive-search');
        if (!resultsEl) throw new Error('Section markup missing');

        const resultsMarkup = resultsEl.innerHTML.replace(/psearch/g, this.input.id);

        // Zweite Absicherung gegen veraltete Antworten: Wenn der Nutzer
        // inzwischen etwas anderes im Feld stehen hat, wird nichts gerendert.
        if (this.getQuery() !== searchTerm) return;

        this.cachedResults[queryKey] = resultsMarkup;
        this.renderResults(resultsMarkup);
      } catch (error) {
        if (error.name === 'AbortError') return;
        this.close();
        throw error;
      }
    }

    setLiveRegionLoadingState() {
      this.setLiveRegionText(this.loadingText);
    }

    setLiveRegionText(statusText) {
      this.statusEl.setAttribute('aria-hidden', 'false');
      this.statusEl.textContent = statusText;
      setTimeout(() => this.statusEl.setAttribute('aria-hidden', 'true'), 1000);
    }

    /**
     * Platzhalterzeilen waehrend des Ladens. Feste Zeilenhoehe, damit das
     * Layout beim Austausch nicht springt.
     */
    showSkeleton() {
      let rows = '';
      for (let i = 0; i < AGT_SEARCH.skeletonRows; i += 1) {
        rows += `
          <div class="agt-skel__row">
            <div class="agt-skel__media"></div>
            <div class="agt-skel__lines">
              <div class="agt-skel__line agt-skel__line--title"></div>
              <div class="agt-skel__line agt-skel__line--meta"></div>
            </div>
          </div>`;
      }

      this.results.innerHTML = `
        <div class="predictive-search absolute bg-theme-bg text-theme-text text-start">
          <div class="predictive-search__scroller overflow-auto">
            <div class="predictive-search__results flex flex-col" aria-hidden="true">
              <div class="agt-skel">
                <div class="agt-skel__group-title"></div>
                ${rows}
              </div>
            </div>
          </div>
        </div>`;

      this.showResults();
      this.dataset.state = 'loading';
      this.open();
    }

    renderResults(resultsMarkup) {
      this.results.innerHTML = resultsMarkup;
      this.setAttribute('results', 'true');
      this.showResults();
      this.dataset.state = 'results';
      this.activeIndex = -1;
      this.open();
    }

    /**
     * Zeigt den Ergebnis-Container, blendet den leeren Zustand aus.
     */
    showResults() {
      this.results.hidden = false;
      if (this.emptyState) this.emptyState.hidden = true;
    }

    /**
     * Zeigt Top-Produkte, solange nichts oder zu wenig getippt wurde.
     * Ohne konfigurierten leeren Zustand wird geschlossen wie bisher.
     */
    showEmptyState() {
      if (!this.emptyState) {
        this.close();
        return;
      }

      this.results.hidden = true;
      this.emptyState.hidden = false;
      this.dataset.state = 'empty';
      this.activeIndex = -1;
      this.open();
    }

    open() {
      this.overlay.classList.add('is-visible');
      this.resetBtn.hidden = !this.input.value.length;
      this.input.setAttribute('aria-expanded', 'true');
      this.setAttribute('open', '');
      document.body.classList.add('overlay-predictive-search');

      this.keydownHandler = this.keydownHandler || this.handleKeydown.bind(this);
      this.keyupHandler = this.keyupHandler || this.handleKeyup.bind(this);
      this.resetBtnClickHandler = this.resetBtnClickHandler || this.close.bind(this);
      this.overlayClickHandler = this.overlayClickHandler || this.close.bind(this);

      this.addEventListener('keydown', this.keydownHandler);
      this.addEventListener('keyup', this.keyupHandler);
      this.resetBtn.addEventListener('click', this.resetBtnClickHandler);
      this.overlay.addEventListener('click', this.overlayClickHandler);
    }

    close(evt) {
      if (evt && evt.target === this.resetBtn) {
        this.input.value = '';
        this.input.classList.remove('search__input--dirty');
        this.removeAttribute('results');
        this.input.focus();
        this.showEmptyState();
        return;
      }

      this.abortRequest();
      clearTimeout(this.debounceTimer);

      const selected = this.querySelector('.predictive-search__item[aria-selected="true"]');
      if (selected) selected.setAttribute('aria-selected', 'false');
      this.activeIndex = -1;

      this.removeAttribute('open');
      delete this.dataset.state;

      // Das X haengt am Feldinhalt, nicht am geoeffneten Dropdown.
      // Vorher stand hier hidden = true - dadurch verschwand das X bei
      // einem einzelnen Zeichen wieder, weil handleInput dann close() ruft.
      this.resetBtn.hidden = !this.input.value.length;

      this.overlay.classList.remove('is-visible');
      this.input.setAttribute('aria-activedescendant', '');
      this.input.setAttribute('aria-expanded', 'false');
      document.body.classList.remove('overlay-predictive-search');

      // keyup fehlte in der Theme-Version und blieb bei jedem Zyklus haengen.
      this.removeEventListener('keydown', this.keydownHandler);
      this.removeEventListener('keyup', this.keyupHandler);
      this.resetBtn.removeEventListener('click', this.resetBtnClickHandler);
      this.overlay.removeEventListener('click', this.overlayClickHandler);
    }
  }

  customElements.define('predictive-search', PredictiveSearch);
}